"use client";

import { KeyboardEvent, MouseEvent, ReactNode, useState, useEffect, useRef } from "react";
import { Button } from "../../elements/Button";
import { ButtonVariants } from "../../shared/Button";
import { Icon, Icons } from "../../elements/Icon";
import { styles } from "./Modal.style";
import { BackShadow } from "../../elements/BackShadow";
import { keyboardAction, KeyType } from "../../core/utils/keyboardActions";
import { cx } from "../../../styled-system/css/cx";

export type ModalSize = "short" | "medium" | "default";

export interface ModalProps {
  isOpen: boolean;
  onClose: (event: MouseEvent | KeyboardEvent) => void;
  closeText?: string;
  children?: ReactNode;
  size?: ModalSize;
  disableOutsideClick?: boolean;
  allowOverflow?: boolean;
  className?: string;
  testSelector?: string;
  closeTestSelector?: string;
}

export const Modal = (props: ModalProps) => {
  const {
    isOpen,
    onClose,
    closeText,
    size = "default",
    children,
    disableOutsideClick = false,
    allowOverflow = false,
    className,
    testSelector,
    closeTestSelector,
  } = props;
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef<number | null>(null);
  const firstFocusableElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      if (firstFocusableElementRef.current) {
        firstFocusableElementRef.current.focus();
      }
    } else {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isOpen]);

  const closeModal = (e: MouseEvent | KeyboardEvent<HTMLDivElement>) => {
    setIsAnimating(false);
    if (isOpen) {
      timerRef.current = window.setTimeout(() => {
        onClose(e);
      }, 250);
    } else {
      onClose(e);
    }
  };

  const handleClickOnBackshadow = (e: MouseEvent<HTMLDivElement>) => {
    if (!disableOutsideClick) {
      closeModal(e);
    }
  };

  return (
    <>
      {isOpen && (
        <div onClick={handleClickOnBackshadow}>
          <div
            className={cx(styles.root(size, isAnimating, allowOverflow), className)}
            onClick={(e) => e.stopPropagation()}
            data-testid={testSelector}
          >
            <div
              className={styles.iconButton()}
              onClick={closeModal}
              onKeyDown={(event) =>
                keyboardAction(event, KeyType.ENTER, () => {
                  closeModal(event);
                })
              }
              ref={firstFocusableElementRef}
              tabIndex={0}
              data-testid={closeTestSelector}
            >
              <Icon icon={Icons.closeBlue} />
            </div>
            {children}
            {!!closeText && (
              <div
                className={styles.closeButtonWrapper()}
                tabIndex={0}
                onKeyDown={(event) =>
                  keyboardAction(event, KeyType.ENTER, () => {
                    closeModal(event);
                  })
                }
              >
                <Button variant={ButtonVariants.TextDecorated} onClick={closeModal}>
                  {closeText}
                </Button>
              </div>
            )}
          </div>
          <BackShadow isBehindModal={true} />
        </div>
      )}
    </>
  );
};
